import { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthModal } from '@/components/AuthModal';
import { ContactModal } from '@/components/ContactModal';
import { LegalModal } from '@/components/LegalModal';

import { useAuth } from '@/hooks/useAuth';
import {
  ArrowRight, ExternalLink, Menu, X, Linkedin,
  FileText, Map, UserCircle, BarChart3, Rocket,
  Brain, Scale, Target, Briefcase, ClipboardList,
  Search, Palette, Settings,
  FileEdit, Pencil, Download,
  Crosshair, FileType, Users, ShieldAlert, GitBranch, FlaskConical, LayoutGrid } from
'lucide-react';
import logoIcon from '@/assets/logo-icon.png';
import logoFull from '@/assets/logo-full.png';
import pixQrCode from '@/assets/pix-qrcode.jpeg';
import AnimatedSection from '@/components/AnimatedSection';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription } from
'@/components/ui/dialog';

const CATEGORIES = [
{ name: 'Estratégia', icon: Target },
{ name: 'Especificação', icon: FileText },
{ name: 'Planejamento', icon: Map },
{ name: 'Governança', icon: ShieldAlert },
{ name: 'UX & Discovery', icon: Search },
{ name: 'Priorização', icon: BarChart3 },
{ name: 'Discovery Avançado', icon: GitBranch },
{ name: 'Métricas & Performance', icon: BarChart3 },
{ name: 'Backlog & Estruturação', icon: LayoutGrid }];


const STEPS = [
{
  number: '01',
  title: 'Escolha um template',
  description: 'Selecione entre modelos estruturados como PRD, Roadmap, Persona, Business Model Canvas e mais.',
  icon: FileEdit,
  mockLines: ['PRD', 'Roadmap', 'Persona', 'Release Plan', 'Risk Log', 'BMC'],
  mockIcons: [FileText, Map, UserCircle, Rocket, ClipboardList, LayoutGrid],
  benefit: 'Estrutura pronta para começar'
},
{
  number: '02',
  title: 'Estruture seu documento',
  description: 'Preencha campos orientados a decisão — organização clara, documentação padronizada e menos retrabalho.',
  icon: Pencil,
  mockFields: ['Visão do produto', 'Problema', 'Hipóteses', 'Métricas de sucesso'],
  benefit: 'Clareza e alinhamento'
},
{
  number: '03',
  title: 'Exporte e compartilhe',
  description: 'Exporte em PDF, DOCX ou PNG e compartilhe com seu time e stakeholders.',
  icon: Download,
  mockFormats: ['PDF', 'DOCX', 'PNG'],
  benefit: 'Pronto para compartilhar'
}];


const DOC_TYPES = [
{ label: 'PRD', icon: FileText },
{ label: 'Roadmap', icon: Map },
{ label: 'Persona', icon: UserCircle },
{ label: 'Release Plan', icon: Rocket },
{ label: 'Métricas', icon: BarChart3 }];



const Header = ({
  onLogin,
  onSupport,
  isLoggedIn
}: {onLogin: () => void;onSupport: () => void;isLoggedIn: boolean;}) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  const navLinkClass = "relative text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-[-2px] after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card/90 backdrop-blur-md border-b border-border">
      <div className="max-w-[1360px] mx-auto h-14 flex items-center justify-between px-[16px]">
        {/* Logo */}
        <div className="flex items-center gap-2.5">
          <button onClick={() => navigate('/')} className="shrink-0 bg-white rounded-xl p-1 shadow-sm border border-border/50 transition-transform duration-200 hover:scale-105" aria-label="Ir para home">
            <img alt="ProductDocs" className="h-8 w-8 object-contain" src="/logo-icon.png" width={32} height={32} fetchPriority="high" />
          </button>
          <span className="font-display text-base font-bold tracking-tight select-none">
            <span className="text-primary">Product</span>
            <span className="text-secondary">Docs</span>
          </span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6">
          <button onClick={() => navigate('/biblioteca')} className={navLinkClass}>
            Biblioteca
          </button>
          {isLoggedIn ?
          <>
              <button onClick={() => navigate('/meus-documentos')} className={navLinkClass}>
                Meus documentos
              </button>
              <button onClick={() => navigate('/conta')} className={navLinkClass}>
                Perfil
              </button>
            </> :

          <>
              <button onClick={() => scrollToSection('como-funciona')} className={navLinkClass}>
                Como funciona
              </button>
              <button onClick={onLogin} className={navLinkClass}>
                Entrar
              </button>
            </>
          }
          <button
            onClick={() => navigate('/biblioteca')}
            className="text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 px-4 py-2 rounded-full transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0">
            Criar documento
          </button>
        </nav>

        {/* Mobile hamburger */}
        <button className="md:hidden text-foreground" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen &&
      <div className="md:hidden bg-card border-b border-border px-4 pb-4 space-y-3 animate-fade-in">
          <button onClick={() => {setMobileOpen(false);navigate('/biblioteca');}} className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1.5">Biblioteca</button>
          {isLoggedIn ?
        <>
              <button onClick={() => {setMobileOpen(false);navigate('/meus-documentos');}} className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1.5">Meus documentos</button>
              <button onClick={() => {setMobileOpen(false);navigate('/conta');}} className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1.5">Perfil</button>
            </> :

        <>
              <button onClick={() => scrollToSection('como-funciona')} className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1.5">Como Funciona</button>
              <button onClick={() => {setMobileOpen(false);onLogin();}} className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-1.5">Entrar</button>
            </>
        }
          <button onClick={() => {setMobileOpen(false);navigate('/biblioteca');}} className="block w-full text-left text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 px-4 py-2 rounded-full">Criar documento</button>
        </div>
      }
    </header>);

};


/* ─── Mock document cards for hero ─── */
const MockDocCard = ({ title, icon: Icon, lines, delay, className }: {title: string;icon: React.ElementType;lines: number[];delay: number;className?: string;}) =>
<div
  className={`bg-card border border-border rounded-xl p-5 shadow-lg w-52 sm:w-56 ${className || ''}`}
  style={{ animation: `float-card 0.7s ease-out ${delay}s both` }}>
  
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--gradient-primary)' }}>
        <Icon className="w-3.5 h-3.5 text-primary-foreground" />
      </div>
      <span className="text-xs font-semibold text-foreground">{title}</span>
    </div>
    <div className="space-y-2">
      {lines.map((w, i) =>
    <div key={i} className="h-2 rounded-full bg-muted" style={{ width: `${w}%` }} />
    )}
    </div>
  </div>;


const HeroSection = ({ onStart, onExplore }: {onStart: () => void;onExplore: () => void;}) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current || !glowRef.current) return;
      const y = window.scrollY;
      const rect = heroRef.current.getBoundingClientRect();
      if (rect.bottom < 0) return;
      glowRef.current.style.transform = `translate(-50%, ${y * 0.3}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section ref={heroRef} className="relative pt-32 pb-16 px-4 overflow-hidden">
    {/* Animated gradient background - PERMANENT */}
    <div className="absolute inset-0 bg-gradient-to-b from-accent/60 via-card to-card" />
    <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-secondary/[0.02] animated-gradient" />
    <div ref={glowRef} className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[700px] rounded-full hero-glow blur-[120px] will-change-transform" style={{ background: 'var(--gradient-primary)' }} />
    
    <div className="max-w-4xl mx-auto text-center relative z-10">
      <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-[1.15] mb-8 hero-stagger hero-stagger-1" style={{ textShadow: '0 1px 2px hsl(var(--background) / 0.5)' }}>
        Crie PRDs, Roadmaps e Personas em minutos
      </h1>
      <p className="text-base md:text-lg text-muted-foreground mb-3 max-w-lg mx-auto leading-relaxed hero-stagger hero-stagger-2">
        Templates estruturados para times de produto documentarem decisões com clareza.
      </p>
      <p className="text-sm text-muted-foreground/70 mb-10 max-w-md mx-auto leading-relaxed hero-stagger hero-stagger-3">
        Organize problemas, hipóteses, trade-offs, riscos e métricas sem partir do zero.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 hero-stagger hero-stagger-4">
        <button onClick={onStart} className="btn-elevated inline-flex items-center justify-center gap-2 w-56 py-4 text-sm font-semibold text-primary-foreground rounded-full" style={{ background: 'var(--gradient-primary)' }}>
          Começar gratuitamente
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
            onClick={onExplore}
            className="btn-secondary-elevated inline-flex items-center justify-center gap-2 w-56 py-4 text-sm font-semibold text-primary border border-primary/30 rounded-full bg-transparent">
          Explorar templates
        </button>
      </div>


      {/* Mockup document cards */}
      <div className="relative mt-14 mb-4 flex items-end justify-center gap-4 sm:gap-6 hero-stagger hero-stagger-6">
        <MockDocCard
            title="PRD"
            icon={FileText}
            lines={[90, 75, 60, 85, 45]}
            delay={0.5}
            className="-rotate-3 translate-y-2 opacity-80 hidden sm:block" />
          
        <MockDocCard
            title="Roadmap"
            icon={Map}
            lines={[80, 95, 70, 50, 85, 60]}
            delay={0.6}
            className="z-10 scale-105 shadow-xl" />
          
        <MockDocCard
            title="Persona"
            icon={UserCircle}
            lines={[70, 85, 55, 90, 40]}
            delay={0.7}
            className="rotate-3 translate-y-2 opacity-80 hidden sm:block" />
          
      </div>
    </div>
  </section>);
};


const TeseSection = () =>
<AnimatedSection>
  <section className="py-28 px-4 bg-accent">
    <div className="max-w-5xl mx-auto">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-14 text-center">
        Documentação é instrumento de decisão
      </h2>
      <div className="grid sm:grid-cols-3 gap-8">
        {[
        { icon: Brain, title: 'Estrutura pensamento', desc: 'Organize hipóteses e decisões antes da execução.' },
        { icon: Scale, title: 'Registra trade-offs', desc: 'Documente impactos, riscos e alternativas.' },
        { icon: Target, title: 'Reduz ambiguidade', desc: 'Garanta alinhamento antes do desenvolvimento.' }].
        map((item, i) =>
        <AnimatedSection key={item.title} delay={0.1 * i}>
            <div className="landing-card flex flex-col items-center text-center rounded-2xl p-6 border border-transparent group">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 border-2 border-primary/15 bg-card shadow-sm icon-scale" style={{ background: 'var(--gradient-primary)' }}>
                <item.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-2">{item.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-[220px]">{item.desc}</p>
            </div>
          </AnimatedSection>
        )}
      </div>
      <AnimatedSection delay={0.35}>
        <p className="text-center text-sm text-foreground font-medium mt-14 max-w-xl mx-auto border-t border-border/50 pt-8 leading-relaxed">
          O <span className="font-display font-bold tracking-tight"><span className="text-primary">Product</span><span className="text-secondary">Docs</span></span> organiza cada documento com foco em clareza, impacto, risco e coerência estratégica — transformando escrita em decisão estruturada.
        </p>
      </AnimatedSection>
    </div>
  </section>
</AnimatedSection>;


const ROLES = [
{ icon: Briefcase, label: 'Product Managers' },
{ icon: ClipboardList, label: 'Product Owners' },
{ icon: Search, label: 'Analistas de Negócio' },
{ icon: Palette, label: 'Product Designers' },
{ icon: Settings, label: 'Tech Leads' },
{ icon: LayoutGrid, label: 'Project Managers' }];


const PublicoAlvoSection = () =>
<AnimatedSection delay={0.1}>
  <section className="py-28 px-4 bg-card">
    <div className="max-w-5xl mx-auto text-center">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-12">
        Estrutura pensada para times de produto
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-10 max-w-5xl mx-auto">
        {ROLES.map((role, i) =>
        <AnimatedSection key={role.label} delay={0.06 * i} className="h-full">
            <div className="landing-card flex items-center justify-center gap-2 md:gap-2.5 px-3 md:px-5 py-3 rounded-xl border border-border bg-accent h-full">
              <role.icon className="w-4 h-4 md:w-4.5 md:h-4.5 text-primary shrink-0" />
              <span className="text-xs md:text-sm font-medium text-foreground text-center">{role.label}</span>
            </div>
          </AnimatedSection>
        )}
      </div>
      <p className="text-muted-foreground text-sm md:text-base leading-relaxed max-w-lg mx-auto">
        O <span className="font-display font-bold tracking-tight"><span className="text-primary">Product</span><span className="text-secondary">Docs</span></span> organiza decisões de produto e iniciativas em contextos onde clareza, alinhamento e rastreabilidade são essenciais.
      </p>
    </div>
  </section>
</AnimatedSection>;


const DiferenciacaoSection = () =>
<AnimatedSection delay={0.1}>
  <section className="py-28 px-4 bg-accent">
    <div className="max-w-5xl mx-auto">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-12 text-center">
        Ferramentas genéricas armazenam texto<br />
        <span className="font-display font-bold tracking-tight"><span className="text-primary">Product</span><span className="text-secondary">Docs</span></span><span className="text-secondary"> estrutura decisões</span>
      </h2>
      <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-12">
        {/* Generic tools */}
        <AnimatedSection delay={0.1}>
          <div className="landing-card rounded-2xl border border-border bg-card p-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-4 icon-scale group-hover:scale-110">
              <FileType className="w-6 h-6 text-muted-foreground" />
            </div>
            <h3 className="text-sm font-semibold text-foreground mb-2">Ferramentas genéricas</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">Armazenam texto sem estrutura de produto. O resultado é desalinhamento, retrabalho e perda de contexto.</p>
          </div>
        </AnimatedSection>
        {/* ProductDocs */}
        <AnimatedSection delay={0.2}>
          <div className="landing-card rounded-2xl border-2 border-primary/20 bg-card p-6 text-center shadow-sm">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 bg-white shadow-sm border border-border/50 overflow-hidden icon-scale">
              <img src={logoIcon} alt="ProductDocs" className="w-7 h-7 object-contain" />
            </div>
            <h3 className="text-sm font-semibold mb-2"><span className="text-primary">Product</span><span className="text-secondary">Docs</span></h3>
            <p className="text-xs text-muted-foreground leading-relaxed">Estrutura pronta para documentação de produto — conectando visão, estratégia, especificação e planejamento.</p>
          </div>
        </AnimatedSection>
      </div>
      <AnimatedSection delay={0.3}>
        <p className="text-center text-sm text-foreground font-medium max-w-xl mx-auto leading-relaxed">
          O <span className="font-display font-bold tracking-tight"><span className="text-primary">Product</span><span className="text-secondary">Docs</span></span> nasce com estrutura pronta para documentação de produto — conectando visão, estratégia, especificação e planejamento em um fluxo coerente.
        </p>
      </AnimatedSection>
    </div>
  </section>
</AnimatedSection>;


const TEMPLATE_PREVIEWS = [
{ name: 'PRD', icon: FileText },
{ name: 'Roadmap', icon: Map },
{ name: 'Persona', icon: UserCircle },
{ name: 'Release Plan', icon: Rocket },
{ name: 'Business Model Canvas', icon: LayoutGrid }];


const BibliotecaSection = ({ onExplore }: {onExplore: () => void;}) =>
<AnimatedSection>
  <div />
</AnimatedSection>;


const QUICK_ACTIONS = [
{ id: 'prd', icon: FileText, label: 'Criar um PRD', description: 'Documente requisitos de produto', state: { autoSelect: 'prd' } },
{ id: 'roadmap', icon: Map, label: 'Criar um Roadmap', description: 'Planeje iniciativas por trimestre', state: { autoSelect: 'roadmap' } },
{ id: 'persona', icon: UserCircle, label: 'Criar uma Persona', description: 'Defina perfis de usuário', state: { autoSelect: 'persona' } },
{ id: 'biblioteca', icon: BarChart3, label: 'Explorar Biblioteca', description: 'Veja todos os templates disponíveis', state: {} }];


/* Mini animated mocks for Como Funciona — miniaturized real UI */
const MOCK_HEIGHT = 'h-[100px]';

const StepMock1 = () =>
<div className={`${MOCK_HEIGHT} bg-card rounded-md border border-border/80 overflow-hidden shadow-xs`}>
    <div className="flex items-center gap-1 px-2 py-1 border-b border-border/60 bg-accent/40">
      <Search className="w-2 h-2 text-muted-foreground/70" />
      <div className="flex-1 h-1.5 rounded-full bg-muted/50" />
    </div>
    <div className="p-1.5 space-y-[3px]">
      {[
    { name: 'PRD', Icon: FileText, active: true },
    { name: 'Roadmap', Icon: Map, active: false },
    { name: 'Persona', Icon: UserCircle, active: false }].
    map((t, i) =>
    <div key={t.name} className={`flex items-center justify-between px-1.5 py-[3px] rounded text-[8px] step-highlight ${t.active ? 'bg-primary/10 border border-primary/25' : 'border border-transparent'}`} style={{ animationDelay: `${i * 0.15}s` }}>
          <div className="flex items-center gap-1">
            <div className={`w-3.5 h-3.5 rounded flex items-center justify-center ${t.active ? 'bg-primary/15' : 'bg-muted/40'}`}>
              <t.Icon className={`w-2 h-2 ${t.active ? 'text-primary' : 'text-muted-foreground/60'}`} />
            </div>
            <span className={t.active ? 'font-medium text-primary' : 'text-muted-foreground/70'}>{t.name}</span>
          </div>
          {t.active &&
      <span className="text-[6px] text-primary-foreground bg-primary px-1 py-0.5 rounded-sm font-medium">Ver modelo</span>
      }
        </div>
    )}
    </div>
  </div>;


const StepMock2 = () =>
<div className={`${MOCK_HEIGHT} bg-card rounded-md border border-border/80 overflow-hidden shadow-xs`}>
    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border/60">
      <div className="w-3.5 h-3.5 rounded flex items-center justify-center" style={{ background: 'var(--gradient-primary)' }}>
        <FileText className="w-2 h-2 text-primary-foreground" />
      </div>
      <span className="text-[8px] font-semibold text-foreground">PRD — Requirements</span>
    </div>
    <div className="p-2 space-y-[5px]">
      {['Contexto', 'Problema', 'Objetivo'].map((section, i) =>
    <div key={section} className="step-line" style={{ animationDelay: `${i * 0.2}s` }}>
          <div className="text-[7px] font-medium text-foreground/80 mb-[2px]">{section}</div>
          <div className="space-y-[2px]">
            <div className="h-[3px] rounded-full bg-muted/70" style={{ width: `${90 - i * 12}%` }} />
            <div className="h-[3px] rounded-full bg-muted/40" style={{ width: `${65 - i * 8}%` }} />
          </div>
        </div>
    )}
    </div>
  </div>;


const StepMock3 = () =>
<div className={`${MOCK_HEIGHT} bg-card rounded-md border border-border/80 overflow-hidden shadow-xs`}>
    <div className="flex items-center justify-between px-2 py-1.5 border-b border-border/60">
      <span className="text-[8px] font-semibold text-foreground">Novo PRD</span>
      <div className="flex items-center gap-0.5 text-[6px] text-primary-foreground bg-primary px-1.5 py-0.5 rounded-sm font-medium step-save">
        <Pencil className="w-1.5 h-1.5" />
        Salvar
      </div>
    </div>
    <div className="p-2 space-y-[5px]">
      {[
    { label: 'Visão do produto', filled: true },
    { label: 'Problema', filled: true },
    { label: 'Hipóteses', filled: false }].
    map((f, i) =>
    <div key={f.label} className="step-fill" style={{ animationDelay: `${i * 0.2}s` }}>
          <div className="text-[7px] text-muted-foreground/80 mb-[2px]">{f.label}</div>
          <div className={`h-3.5 rounded border px-1 flex items-center ${f.filled ? 'border-primary/25 bg-primary/5' : 'border-border/60 bg-accent/20'}`}>
            {f.filled && <div className="h-[3px] rounded-full bg-foreground/20" style={{ width: f.label === 'Visão do produto' ? '80%' : '55%' }} />}
          </div>
        </div>
    )}
    </div>
  </div>;


const StepMock4 = () =>
<div className={`${MOCK_HEIGHT} bg-card rounded-md border border-border/80 overflow-hidden shadow-xs`}>
    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border/60">
      <ClipboardList className="w-2.5 h-2.5 text-primary" />
      <span className="text-[8px] font-semibold text-foreground">Meus documentos</span>
    </div>
    <div className="p-1.5 space-y-[3px]">
      {[
    { name: 'PRD — App Mobile', Icon: FileText, date: '09 Mar' },
    { name: 'Roadmap Q2', Icon: Map, date: '08 Mar' },
    { name: 'Persona — Ana', Icon: UserCircle, date: '07 Mar' }].
    map((doc, i) =>
    <div key={doc.name} className="flex items-center gap-1.5 px-1.5 py-[3px] rounded bg-accent/30 border border-border/50 step-doc" style={{ animationDelay: `${i * 0.15}s` }}>
          <div className="w-3.5 h-3.5 rounded flex items-center justify-center bg-primary/10">
            <doc.Icon className="w-2 h-2 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[7px] font-medium text-foreground truncate">{doc.name}</div>
            <div className="text-[6px] text-muted-foreground/70">{doc.date}</div>
          </div>
        </div>
    )}
    </div>
  </div>;


const StepMock5 = () =>
<div className={`${MOCK_HEIGHT} bg-card rounded-md border border-border/80 overflow-hidden shadow-xs`}>
    <div className="flex items-center px-2 py-1.5 border-b border-border/60">
      <span className="text-[8px] font-semibold text-foreground">Exportar documento</span>
    </div>
    <div className="p-2 space-y-[3px]">
      {[
    { fmt: 'PDF', Icon: FileText, active: true },
    { fmt: 'DOCX', Icon: FileEdit, active: false },
    { fmt: 'PNG', Icon: BarChart3, active: false }].
    map((exp, i) =>
    <div key={exp.fmt} className={`flex items-center justify-between px-1.5 py-[4px] rounded text-[8px] font-medium border step-export ${exp.active ? 'border-primary/30 bg-primary/10 text-primary' : 'border-border/50 text-muted-foreground/70'}`} style={{ animationDelay: `${i * 0.2}s` }}>
          <div className="flex items-center gap-1">
            <exp.Icon className={`w-2 h-2 ${exp.active ? 'text-primary' : 'text-muted-foreground/50'}`} />
            <span>{exp.fmt}</span>
          </div>
          <Download className={`w-2 h-2 ${exp.active ? 'text-primary' : 'text-muted-foreground/40'}`} />
        </div>
    )}
    </div>
  </div>;


const ComoFuncionaSection = () => {
  const navigate = useNavigate();

  const steps = [
  { number: '01', title: 'Escolha um template', description: 'Selecione entre PRD, Roadmap, Persona e mais.', icon: FileEdit, Mock: StepMock1 },
  { number: '02', title: 'Veja o modelo', description: 'Consulte a estrutura completa do template.', icon: FileText, Mock: StepMock2 },
  { number: '03', title: 'Estruture seu documento', description: 'Preencha campos orientados a decisão.', icon: Pencil, Mock: StepMock3 },
  { number: '04', title: 'Salve em Meus documentos', description: 'Armazene e acesse quando precisar.', icon: ClipboardList, Mock: StepMock4 },
  { number: '05', title: 'Exporte e compartilhe', description: 'Exporte em PDF, DOCX ou PNG.', icon: Download, Mock: StepMock5 }];


  return (
    <AnimatedSection>
    <section id="como-funciona" className="py-28 px-4 bg-card">
      <div className="max-w-[1360px] mx-auto">
        <div className="text-center mb-14">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Como funciona
          </h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            Cinco passos para transformar ideias em decisões estruturadas.
          </p>
        </div>

        <div className="relative">
          {/* Connector line — centered on step number row */}
          <div className="hidden lg:block absolute top-[22px] left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent z-0" />

          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {steps.map((step, i) =>
              <AnimatedSection key={step.number} delay={0.08 * i}>
                <div className="landing-card h-full flex flex-col bg-card rounded-2xl border border-border p-4 group">
                  {/* Step header */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 icon-scale" style={{ background: 'var(--gradient-primary)' }}>
                      <step.icon className="w-3.5 h-3.5 text-primary-foreground" />
                    </div>
                    <span className="text-sm font-display font-bold text-primary">{step.number}</span>
                  </div>
                  
                  {/* Mock — fixed height container */}
                  <div className="mb-3">
                    <step.Mock />
                  </div>
                  
                  {/* Text */}
                  <h3 className="text-xs font-semibold text-foreground mb-1 leading-snug">{step.title}</h3>
                  <p className="text-[10px] text-muted-foreground leading-relaxed flex-1">{step.description}</p>
                </div>
              </AnimatedSection>
              )}
          </div>
        </div>

        {/* CTA */}
        <AnimatedSection delay={0.45}>
          <div className="text-center mt-12">
            <button
                onClick={() => navigate('/biblioteca')}
                className="btn-elevated inline-flex items-center gap-2 px-7 py-4 text-sm font-semibold text-primary-foreground rounded-full"
                style={{ background: 'var(--gradient-primary)' }}>
                
              Explorar templates
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  </AnimatedSection>);

};


const Footer = ({ onContact, onTermos, onPrivacidade, onSupport }: {onContact: () => void;onTermos: () => void;onPrivacidade: () => void;onSupport: () => void;}) => {
  return (
    <footer className="py-12 px-4 border-t border-border bg-accent">
    <div className="max-w-5xl mx-auto text-center space-y-4">
      <p className="text-sm text-muted-foreground">
        <span className="font-display font-bold tracking-tight"><span className="text-primary">Product</span><span className="text-secondary">Docs</span></span> é um produto da <span className="font-medium text-foreground">Duarte Consultoria e Tecnologia</span>.
      </p>
      <p className="text-sm text-muted-foreground text-center font-extralight inline-flex items-center justify-center gap-1">
        Fundado por{' '}
        <a
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-foreground font-medium hover:text-primary transition-colors duration-200" href="https://www.linkedin.com/in/duartepriscila">
           Priscila Duarte
           <Linkedin className="h-4 w-4 text-[#0A66C2]" />
        </a>
      </p>
      <div className="flex items-center justify-center gap-4 pt-2 flex-wrap">
        <button onClick={onTermos} className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 underline underline-offset-2">
          Termos de Uso
        </button>
        <span className="text-xs text-muted-foreground/40">•</span>
        <button onClick={onPrivacidade} className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 underline underline-offset-2">
          Política de Privacidade
        </button>
        <span className="text-xs text-muted-foreground/40">•</span>
        <button onClick={onContact} className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 underline underline-offset-2">
          Fale conosco
        </button>
        <span className="text-xs text-muted-foreground/40">•</span>
        <button onClick={onSupport} className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 underline underline-offset-2">
          Apoiar projeto
        </button>
      </div>
    </div>
  </footer>);
};


const PIX_KEY = '00020126580014BR.GOV.BCB.PIX0136400d2c33-7f7a-4872-b766-ff2babcd13605204000053039865802BR5921PRISCILA COSTA DUARTE6011SANTA LUZIA622605226dRlpEWAgOz86ZTjJUobrt63046429';

const SupportModal = ({ open, onOpenChange }: {open: boolean;onOpenChange: (v: boolean) => void;}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(PIX_KEY).then(() => {
      setCopied(true);
      toast.success('Chave copiada com sucesso.');
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const suggestions = [
  { emoji: '🧩', value: 'R$5', label: 'menos retrabalho para alguém' },
  { emoji: '📄', value: 'R$10', label: 'mais clareza nas decisões' },
  { emoji: '🚀', value: 'R$20+', label: 'uma pequena feature para o futuro' }];


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto p-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-2 text-center space-y-2">
          <DialogHeader>
            <DialogTitle className="font-display text-xl inline-flex items-center justify-center gap-2">
              <span className="text-destructive">♥</span> Apoie o <span className="text-primary">Product</span><span className="text-secondary">Docs</span>
            </DialogTitle>
            <DialogDescription className="sr-only">
              Contribua voluntariamente para apoiar o desenvolvimento do ProductDocs
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Uma ferramenta gratuita para estruturar decisões de produto.
          </p>
        </div>

        <div className="px-6 pb-6 space-y-6">
          {/* Intro text */}
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              Se o projeto te ajuda no dia a dia, você pode apoiar sua evolução com uma contribuição voluntária.
            </p>
            <p className="text-xs">
              A plataforma continuará gratuita. A contribuição não libera funcionalidades extras — é apenas uma forma de apoiar o desenvolvimento contínuo.
            </p>
          </div>

          {/* Suggestion cards */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-foreground tracking-wide uppercase">Sugestão de apoio</p>
            <div className="grid gap-3">
              {suggestions.map((item) =>
              <div
                key={item.value}
                className="flex items-center gap-4 rounded-xl border border-border bg-muted/40 px-5 py-4 text-sm shadow-sm hover:shadow-md hover:border-primary/30 hover:-translate-y-0.5 transition-all duration-200 cursor-default">
                
                  <span className="text-2xl">{item.emoji}</span>
                  <div>
                    <span className="font-semibold text-foreground text-base">{item.value}</span>
                    <p className="text-muted-foreground text-xs mt-0.5">{item.label}</p>
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Esses valores são apenas sugestões. Você pode contribuir com qualquer valor.
            </p>
          </div>

          {/* PIX section */}
          <div className="space-y-4 pt-2">
            <div className="text-center">
              <p className="text-sm font-semibold text-foreground">Apoiar via PIX</p>
            </div>

            <div className="flex justify-center">
              <img
                src={pixQrCode}
                alt="QR Code PIX"
                className="w-48 h-48 rounded-xl border border-border shadow-sm" />
              
            </div>

            <p className="text-center text-xs text-muted-foreground">
              Escaneie o QR code ou copie a chave PIX.
            </p>

            <button
              onClick={handleCopy}
              className={`w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
              copied ?
              'bg-green-500/10 text-green-600 border border-green-500/30' :
              'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0'}`
              }>
              
              {copied ? '✓ Chave copiada com sucesso' : 'Copiar chave PIX'}
            </button>
          </div>

          {/* Footer message */}
          <p className="text-center text-xs text-muted-foreground pt-1">
            Se o <span className="font-display font-bold tracking-tight"><span className="text-primary">Product</span><span className="text-secondary">Docs</span></span> já evitou um retrabalho para você, já valeu 😉
          </p>
        </div>
      </DialogContent>
    </Dialog>);

};


const Index = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [legalModal, setLegalModal] = useState<'termos' | 'privacidade' | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('sessionExpired') !== '1') return;

    toast.error('Sua sessão expirou por motivos de segurança. Faça login novamente.');
    setShowAuth(true);

    params.delete('sessionExpired');
    const cleanSearch = params.toString();

    navigate(
      {
        pathname: '/',
        search: cleanSearch ? `?${cleanSearch}` : ''
      },
      { replace: true }
    );
  }, [location.search, navigate]);

  const handleStart = () => {
    if (user) {
      navigate('/biblioteca');
    } else {
      setShowAuth(true);
    }
  };

  const handleExplore = () => {
    navigate('/biblioteca');
  };

  const handleLogin = () => {
    if (user) {
      navigate('/biblioteca');
    } else {
      setShowAuth(true);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header onLogin={handleLogin} onSupport={() => setShowSupport(true)} isLoggedIn={!!user} />
      <main>
        <HeroSection onStart={handleStart} onExplore={handleExplore} />
        <TeseSection />
        <PublicoAlvoSection />
        <DiferenciacaoSection />
        <BibliotecaSection onExplore={handleExplore} />
        <ComoFuncionaSection />
        
      </main>
      <Footer onContact={() => setShowContact(true)} onTermos={() => setLegalModal('termos')} onPrivacidade={() => setLegalModal('privacidade')} onSupport={() => setShowSupport(true)} />
      <AuthModal open={showAuth} onOpenChange={setShowAuth} />
      <ContactModal open={showContact} onOpenChange={setShowContact} />
      <SupportModal open={showSupport} onOpenChange={setShowSupport} />
      <LegalModal open={legalModal !== null} onOpenChange={(open) => !open && setLegalModal(null)} type={legalModal ?? 'termos'} />
    </div>);

};

export default Index;